import { rm } from 'node:fs/promises';
import { join } from 'node:path';
import { Hono } from 'hono';
import { cors } from 'hono/cors';
import { z } from 'zod';
import { JobService } from '../services/job.service.js';
import { spawnBackgroundWorker } from '../workers/spawn-worker.js';
import { renderAdminUI } from './ui/admin.js';
import type { ServiceContainer } from '../services/index.js';
import type { SearchQuery } from '../types/search.js';

// HTTP API validation schemas (consistent with MCP schemas)
const CreateStoreBodySchema = z
  .object({
    name: z.string().min(1, 'Store name must be a non-empty string'),
    type: z.enum(['file', 'repo', 'web']),
    path: z.string().min(1).optional(),
    url: z.string().min(1).optional(),
    description: z.string().optional(),
    tags: z.array(z.string()).optional(),
    branch: z.string().optional(),
    depth: z.number().int().positive().optional(),
  })
  .refine(
    (data) => {
      switch (data.type) {
        case 'file':
          return data.path !== undefined;
        case 'web':
          return data.url !== undefined;
        case 'repo':
          return data.path !== undefined || data.url !== undefined;
      }
    },
    {
      message:
        'Missing required field: file stores need path, web stores need url, repo stores need path or url',
    }
  );

const SearchBodySchema = z.object({
  query: z.string().min(1, 'Query must be a non-empty string'),
  detail: z.enum(['minimal', 'contextual', 'full']).optional(),
  limit: z.number().int().positive().optional(),
  stores: z.array(z.string()).optional(),
});

export function createApp(services: ServiceContainer): Hono {
  const app = new Hono();

  app.use('*', cors());

  // Admin UI
  app.get('/', (c) => c.html(renderAdminUI()));

  // Health check
  app.get('/health', (c) => c.json({ status: 'ok' }));

  // Stores
  app.get('/api/stores', async (c) => {
    const stores = await services.store.list();
    return c.json(stores);
  });

  app.post('/api/stores', async (c) => {
    const jsonData: unknown = await c.req.json();
    const parseResult = CreateStoreBodySchema.safeParse(jsonData);
    if (!parseResult.success) {
      return c.json({ error: parseResult.error.issues[0]?.message ?? 'Invalid request body' }, 400);
    }
    const result = await services.store.create(parseResult.data);
    if (!result.success) {
      return c.json({ error: result.error.message }, 400);
    }

    // Spawn background indexing — mirrors MCP handler behavior
    const dataDir = services.config.resolveDataDir();
    const isUrl = parseResult.data.url !== undefined;
    const jobType =
      parseResult.data.type === 'web'
        ? 'crawl'
        : parseResult.data.type === 'repo' && isUrl
          ? 'clone'
          : 'index';

    const storePath = result.data.type !== 'web' ? result.data.path : undefined;
    const jobDetails: Record<string, unknown> = {
      storeName: result.data.name,
      storeId: result.data.id,
      ...(isUrl ? { url: parseResult.data.url } : {}),
      ...(storePath !== undefined ? { path: storePath } : {}),
      phase: jobType === 'crawl' ? 'crawling' : jobType === 'clone' ? 'cloning' : 'indexing',
      phaseStep: 1,
      phaseTotalSteps: jobType === 'index' ? 1 : 2,
    };

    const jobService = new JobService(dataDir);
    const job = jobService.createJob({
      type: jobType,
      details: jobDetails,
      message: `${jobType === 'crawl' ? 'Crawling' : 'Indexing'} ${result.data.name}...`,
    });
    spawnBackgroundWorker(job.id, dataDir);

    return c.json({ ...result.data, job: { id: job.id, status: job.status } }, 201);
  });

  app.get('/api/stores/:id', async (c) => {
    const store = await services.store.getByIdOrName(c.req.param('id'));
    if (!store) return c.json({ error: 'Not found' }, 404);
    return c.json(store);
  });

  app.delete('/api/stores/:id', async (c) => {
    const store = await services.store.getByIdOrName(c.req.param('id'));
    if (!store) return c.json({ error: 'Not found' }, 404);

    // Delete LanceDB table first (so searches don't return results for deleted store)
    await services.lance.deleteStore(store.id);

    // Delete code graph
    await services.codeGraph.deleteGraph(store.id);

    // Delete manifest
    await services.manifest.delete(store.id);

    // For repo stores cloned from URL, remove the cloned directory
    // Only delete if path is within our data directory (cloned repos)
    const resolvedDataDir = services.config.resolveDataDir();
    if (
      store.type === 'repo' &&
      'url' in store &&
      store.url !== undefined &&
      store.path.startsWith(join(resolvedDataDir, 'repos'))
    ) {
      await rm(store.path, { recursive: true, force: true });
    }

    // Delete from registry last
    const result = await services.store.delete(store.id);
    if (result.success) return c.json({ deleted: true });
    return c.json({ error: result.error.message }, 400);
  });

  // Search
  app.post('/api/search', async (c) => {
    const jsonData: unknown = await c.req.json();
    const parseResult = SearchBodySchema.safeParse(jsonData);
    if (!parseResult.success) {
      return c.json({ error: parseResult.error.issues[0]?.message ?? 'Invalid request body' }, 400);
    }

    const storeIds = (await services.store.list()).map((s) => s.id);

    services.lance.setDimensions(await services.embeddings.ensureDimensions());
    for (const id of storeIds) {
      await services.lance.initialize(id);
    }

    // Resolve user-provided store strings to StoreIds, or use all stores
    let requestedStores = storeIds;
    if (parseResult.data.stores !== undefined) {
      const resolvedStores: typeof storeIds = [];
      for (const requested of parseResult.data.stores) {
        const store = await services.store.getByIdOrName(requested);
        if (store === undefined) {
          return c.json({ error: `Store not found: ${requested}` }, 404);
        }
        resolvedStores.push(store.id);
      }
      requestedStores = resolvedStores;
    }

    const query: SearchQuery = {
      query: parseResult.data.query,
      detail: parseResult.data.detail ?? 'minimal',
      limit: parseResult.data.limit ?? 10,
      stores: requestedStores,
    };
    const results = await services.search.search(query);
    return c.json(results);
  });

  // Documents (chunks) for a store
  app.get('/api/stores/:id/documents', async (c) => {
    const store = await services.store.getByIdOrName(c.req.param('id'));
    if (!store) return c.json({ error: 'Not found' }, 404);

    const limit = parseInt(c.req.query('limit') ?? '50', 10);
    const offset = parseInt(c.req.query('offset') ?? '0', 10);

    try {
      services.lance.setDimensions(await services.embeddings.ensureDimensions());
      await services.lance.initialize(store.id);
      const count = await services.lance.countDocuments(store.id);
      const documents = await services.lance.getAllDocuments(store.id, { limit, offset });
      return c.json({ documents, total: count, limit, offset });
    } catch {
      // Store may not be indexed yet
      return c.json({ documents: [], total: 0, limit, offset });
    }
  });

  // Index
  app.post('/api/stores/:id/index', async (c) => {
    const store = await services.store.getByIdOrName(c.req.param('id'));
    if (!store) return c.json({ error: 'Not found' }, 404);

    services.lance.setDimensions(await services.embeddings.ensureDimensions());
    await services.lance.initialize(store.id);
    const result = await services.index.indexStore(store);

    if (result.success) return c.json(result.data);
    return c.json({ error: result.error.message }, 400);
  });

  // Jobs
  app.get('/api/jobs', (c) => {
    const dataDir = services.config.resolveDataDir();
    const jobService = new JobService(dataDir);
    return c.json(jobService.listJobs());
  });

  app.get('/api/jobs/:id', (c) => {
    const dataDir = services.config.resolveDataDir();
    const jobService = new JobService(dataDir);
    const job = jobService.getJob(c.req.param('id'));
    if (!job) return c.json({ error: 'Not found' }, 404);
    return c.json(job);
  });

  return app;
}
